import { useEffect, useState, useCallback } from 'react';
import { useMount, useSessionStorage, useUnmount } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import { DownOutlined } from '@ant-design/icons';
import moment from 'moment';

import PageHeader from '../../uiKitComponents/pageHeader';
import Alert from '../../uiKitComponents/alert';
import Button from '../../uiKitComponents/button';
import Table from '../../uiKitComponents/table';
import Dropdown from '../../uiKitComponents/dropdown';
import Descriptions from '../../uiKitComponents/descriptions';
import FiltersSection from '../../components/filtersSection';
import PaginationSummary from '../../components/PaginationSummary';

import {
  getVerificationSLAReport,
  cleanReports,
  setCleanOnUnmountTrue as setReportsCleanOnUnmountTrue,
} from '../../store/actions/reportPagesAction';
import { getUserFilters } from '../../store/actions/userFiltersAction';
import buildTableColumns from '../../utils/buildTableColumns';
import formattingFilters from '../../utils/formattingFilters';

const disableMoreThan6MonthsAndFuture = (current) => {
  const today = moment();
  const sixMonthsAgo = today.subtract(6, 'months');
  return (
    current &&
    (current.isBefore(sixMonthsAgo, 'day') || current.isAfter(today, 'day'))
  );
};

const initialState = {
  page: 1,
  sort_column: 'id',
  sort_direction: 'descend',
  filters: [{}],
  keyword: undefined,
  filter_info: undefined,
};

const VerificationSLAPage = () => {
  const [state, setState] = useSessionStorage('verification-sla', {
    ...initialState,
  });
  const {
    page,
    sort_column,
    sort_direction,
    filters,
    keyword,
    filter_info,
  } = state;

  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const { cleanOnUnmount, verificationSLA, savedFilters } = useSelector(
    (store) => ({
      cleanOnUnmount: store.reportPages.cleanOnUnmount,
      verificationSLA: store.reportPages.payload.verificationSLA,
      savedFilters: store.userFilters.payload,
    }),
  );

  useMount(() => {
    setReportsCleanOnUnmountTrue()(dispatch);
  });

  useUnmount(() => {
    if (cleanOnUnmount) {
      sessionStorage.removeItem('verification-sla');
      cleanReports()(dispatch);
    }
  });

  const fetchData = useCallback(() => {
    setLoading(true);
    const filterParams = {};
    filters.forEach((filter) =>
      Object.entries(
        formattingFilters(filter, filter.keyword, 'STRING'),
      ).forEach(([key, value]) => {
        filterParams[`filters[${key}]`] = value;
      }),
    );

    getVerificationSLAReport({
      page,
      sort_column,
      sort_direction: sort_direction || 'ascend',
      keyword,
      ...filterParams,
    })(dispatch)
      .catch((err) => {
        if (err?.response?.status === 500) {
          setError({
            status: err.response.status,
            message: err.response.data.message,
          });
          sessionStorage.removeItem('verification-sla');
        }
      })
      .finally(() => setLoading(false));
  }, [dispatch, page, sort_column, sort_direction, keyword, filters]);

  useEffect(() => {
    fetchData();
  }, [dispatch, fetchData]);

  const fetchFilters = useCallback(() => {
    getUserFilters('verification-sla')(dispatch).catch((err) => {
      if (err.response?.status === 500) {
        setError({
          status: err.response.status,
          message: err.response.data.message,
        });
      }
    });
  }, [dispatch, setError]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  const selectSavedFilter = (filterId) => {
    const id = Number(filterId);
    const filter = savedFilters.find((f) => f.id === id);
    if (filter) {
      const newFilters = Object.entries(filter.filters).map(([key, value]) =>
        formattingFilters(key, value, 'OBJECT'),
      );
      setState((prevState) => ({
        ...prevState,
        filters: newFilters,
        keyword: filter.keyword,
        filter_info: filter,
      }));
    }
  };

  const onSubmitFilters = (values) =>
    setState((prevState) => ({
      ...prevState,
      ...values,
    }));

  const clearFilters = () => setState(initialState);

  const onTableChange = useCallback(
    (pagination, _, sorter, { action }) => {
      const newState = {};
      if (action === 'paginate') {
        newState.page = pagination.current;
      }
      if (action === 'sort') {
        newState.sort_column = sorter.field;
        newState.sort_direction = sorter.order;
      }
      setState((prevState) => ({ ...prevState, ...newState }));
    },
    [setState],
  );

  if (error)
    return (
      <PageHeader className="page-main-header" title="Deadline/SLA Control">
        <Alert
          showIcon
          type="error"
          message={`Error ${error.status}`}
          description={error.message}
        />
      </PageHeader>
    );

  return (
    <div>
      <PageHeader
        className="page-main-header"
        title="Deadline/SLA Control"
        extra={[
          <Dropdown
            menu={{
              items: savedFilters?.map((item) => ({
                label: item.name,
                key: item.id,
              })),
              onClick: ({ key }) => selectSavedFilter(key),
            }}
            trigger={['click']}
            placement="bottomRight"
            disabled={!savedFilters.length}
            key="saved_filters"
          >
            <Button loading={!savedFilters}>
              Saved Filters
              <DownOutlined />
            </Button>
          </Dropdown>,
        ]}
      >
        <FiltersSection
          clearFilters={clearFilters}
          onSubmit={onSubmitFilters}
          filters={filters}
          keyword={keyword}
          columnOptions={verificationSLA?.filterable}
          loading={!verificationSLA?.filterable}
          filter_info={filter_info}
          filter_columns={verificationSLA?.columns}
          onUpdateFilter={selectSavedFilter}
          entity="verification-queue"
          disableDate={disableMoreThan6MonthsAndFuture}
        />
      </PageHeader>
      <div
        className="page-main-content d-flex align-items-start"
        style={{ gap: 32 }}
      >
        <Descriptions
          bordered
          size="small"
          title="30 Days SLA Summary"
          labelStyle={{ fontWeight: 500, textTransform: 'capitalize' }}
          column={1}
        >
          <Descriptions.Item label={'Near Deadline'}>
            {verificationSLA?.summary?.nearing_30_day_deadline}
          </Descriptions.Item>
          <Descriptions.Item label={'SLA Breaches'}>
            {verificationSLA?.summary?.sla_breaches_30_day}
          </Descriptions.Item>
        </Descriptions>
        <Descriptions
          bordered
          size="small"
          title="Forced 90 Days SLA Summary"
          labelStyle={{ fontWeight: 500, textTransform: 'capitalize' }}
          column={1}
        >
          {Object.entries(
            verificationSLA?.summary?.forced_day_90_outcomes || {},
          ).map(([key, outcome]) => (
            <Descriptions.Item key={key} label={key.split('_').join(' ')}>
              {outcome}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </div>
      <div className="page-main-content">
        <PaginationSummary meta={verificationSLA?.meta} />
        <Table
          rowKey="id"
          columns={buildTableColumns(verificationSLA?.columns)}
          dataSource={verificationSLA?.data}
          loading={loading}
          onChange={onTableChange}
          pagination={{
            position: ['topRight', 'bottomRight'],
            showSizeChanger: false,
            showQuickJumper: true,
            pageSize: verificationSLA?.meta?.per_page,
            total: verificationSLA?.meta?.total,
            current: verificationSLA?.meta?.current_page,
          }}
        />
        <PaginationSummary meta={verificationSLA?.meta} />
      </div>
    </div>
  );
};

export default VerificationSLAPage;
